<template>
  <main role="main" class="gm-style-page">
    <b-container fluid="xxl" class="py-4 d-flex flex-row overflow-hidden">
      <div class="flex-grow-1">
        <div class="gm-container shadow-sm rounded">
          <!-- Header -->
          <div class="gm-header px-4 py-3 border-bottom d-flex align-items-center justify-content-between">
            <h4 class="m-0 font-weight-bold text-dark-blue d-flex align-items-center">
              <b-icon icon="pencil-square" class="mr-2 text-gm-blue" />
              {{ $t("editMeeting") }}
            </h4>
          </div>

          <div v-if="isLoading" class="py-5 text-center">
            <b-spinner variant="primary" />
          </div>

          <div v-else-if="!activeCustomerSubscription" class="modern-inactive-notice animate-fade-in mx-4 my-4">
            <div class="notice-icon">
              <b-icon icon="exclamation-circle" variant="danger"></b-icon>
            </div>
            <div class="notice-content">
              {{ $t("noActiveSub") }}
            </div>
          </div>

          <b-form v-else id="edit-meeting-form" @submit.prevent="submitForm" class="gm-meeting-form px-4 py-3">
            <!-- 1. Tên cuộc họp (Topic) -->
            <b-row class="gm-form-row align-items-start py-3">
              <b-col md="3" class="gm-label-col">
                <label for="input-name" class="gm-label">
                  {{ $t("meetingName") }} <span class="text-danger">(*)</span>
                </label>
              </b-col>
              <b-col md="9">
                <b-form-textarea id="input-name" v-model="name" type="text" required minlength="2" maxlength="1000"
                  rows="2" class="gm-input" :placeholder="$t('meetingNamePlaceHolder')"></b-form-textarea>
              </b-col>
            </b-row>

            <!-- 2. Thời gian bắt đầu (When) -->
            <b-row class="gm-form-row align-items-center py-3">
              <b-col md="3" class="gm-label-col">
                <label class="gm-label">
                  {{ $t("datetime") }} <span class="text-danger">(*)</span>
                </label>
              </b-col>
              <b-col md="9">
                <b-row class="align-items-center">
                  <b-col md="6" class="pr-md-2 mb-2 mb-md-0">
                    <b-form-datepicker v-model="startDate" value-as-date required :state="startDateValidationStatus"
                      :min="new Date()" no-flip hide-header label-no-date-selected="DD/MM/YYYY" locale="vi"
                      :date-format-options="datePickerFormatOptions" start-weekday="1" class="gm-input w-100"></b-form-datepicker>
                  </b-col>
                  <b-col md="6" class="pl-md-2">
                    <div class="d-flex align-items-center" style="gap: 8px;">
                      <span class="small text-muted flex-shrink-0">{{ $t("on_time") }}</span>
                      <b-select :options="hourOptions" v-model="startHour" required class="gm-input flex-grow-1 w-50"></b-select>
                      <b-select :options="minuteOptions" v-model="startMinute" required class="gm-input flex-grow-1 w-50"></b-select>
                    </div>
                  </b-col>
                </b-row>
                <b-form-invalid-feedback :state="startDateValidationStatus">
                  {{ $t("pleaseSelectValidDate") }}
                </b-form-invalid-feedback>
              </b-col>
            </b-row>

            <!-- 3. Thời lượng cuộc họp (Duration) -->
            <b-row class="gm-form-row align-items-center py-3">
              <b-col md="3" class="gm-label-col">
                <label class="gm-label">
                  {{ $t("meetingDuration") }} <span class="text-danger">(*)</span>
                </label>
              </b-col>
              <b-col md="9">
                <b-row class="align-items-center">
                  <b-col md="6" class="pr-md-2 mb-2 mb-md-0">
                    <div class="d-flex align-items-center" style="gap: 8px;">
                      <b-select :options="durationHourOptions" v-model="durationHour" required
                        :state="durationValidationStatus" class="gm-input flex-grow-1 w-50"></b-select>
                      <b-select :options="durationMinuteOptions" v-model="durationMinute" required
                        :state="durationValidationStatus" class="gm-input flex-grow-1 w-50"></b-select>
                    </div>
                  </b-col>
                  <b-col md="6" class="pl-md-2">
                    <span class="small text-muted">
                      ({{ $t("maximum") }} {{ maxDurationStr }}, {{ maxParticipantsCount }} {{ $t("person") }})
                    </span>
                  </b-col>
                </b-row>
                <b-form-invalid-feedback :state="durationValidationStatus">
                  {{ $t("meetingDurationFormFeedback") }} {{ maxDurationStr }}
                </b-form-invalid-feedback>
              </b-col>
            </b-row>

            <!-- 4. Passcode -->
            <b-row class="gm-form-row align-items-center py-3">
              <b-col md="3" class="gm-label-col">
                <label for="input-access-code" class="gm-label">
                  {{ $t("accessCode") }} <span class="text-danger">(*)</span>
                </label>
              </b-col>
              <b-col md="4" lg="3">
                <b-input-group>
                  <b-form-input id="input-access-code" v-model="accessCode" type="text"
                    class="text-monospace custom-validity-fix gm-input" required minlength="6" maxlength="12" autocomplete="off"
                    pattern="^[A-Za-z0-9]+$" @invalid="$event.target.setCustomValidity($t('accessCodeFormFeedback'))"
                    @input.native="$event.target.setCustomValidity('')"></b-form-input>
                  <b-input-group-append>
                    <b-button variant="outline-secondary" v-b-tooltip class="d-flex align-items-center justify-content-center px-3 gm-append-btn"
                      :title="$t('createRandomAccessCode')" @click="setRandomAccessCode">
                      <b-icon-arrow-repeat />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-col>
            </b-row>

            <!-- 4.5. Số người tham dự tối đa (numOfParticipants) -->
            <b-row class="gm-form-row align-items-center py-3">
              <b-col md="3" class="gm-label-col">
                <label class="gm-label">
                  {{ $t("numOfParticipants") }} <span class="text-danger">(*)</span>
                </label>
              </b-col>
              <b-col md="4" lg="3">
                <b-form-input required type="number" min="1" :max="maxParticipantsCount"
                  v-model="participantsCount" class="gm-input" />
              </b-col>
            </b-row>

            <!-- 5. Người điều hành (Alternative Hosts) -->
            <b-row class="gm-form-row align-items-start py-3">
              <b-col md="3" class="gm-label-col">
                <label class="gm-label">
                  {{ $t("meetingOperator") }} <span class="text-danger">(*)</span>
                </label>
              </b-col>
              <b-col md="9">
                <div v-for="(participant, i) of operators" :key="i" class="gm-operator-card p-3 mb-2 rounded bg-light-zoom border">
                  <b-row class="align-items-end">
                    <b-col md="4" class="mb-2 mb-md-0">
                      <b-form-group class="m-0">
                        <template v-slot:label>
                          <span class="small font-weight-bold text-dark-blue">{{ $t("fullName") }} <span class="text-danger">(*)</span></span>
                        </template>
                        <v-select class="vs-normalizer gm-vselect" label="label" :clearable="false" :filterable="false"
                          :options="participant.typeaheadOptions" :clearSearchOnBlur="() => false"
                          @search="participant.doSearchDebounced" v-on:option:selecting="participant.onTypeaheadSelecting">
                          <template v-slot:search="{ attributes, events }">
                            <input v-bind="attributes" v-on="events" v-model="participant.name" :pattern="fullNamePattern"
                              :placeholder="$t('namePlaceHolder')" class="form-control form-control-sm gm-input" required minlength="2"
                              maxlength="100" />
                          </template>
                        </v-select>
                      </b-form-group>
                    </b-col>
                    <b-col md="4" class="mb-2 mb-md-0">
                      <b-form-group class="m-0">
                        <template v-slot:label>
                          <span class="small font-weight-bold text-dark-blue">Email <span class="text-danger">(*)</span></span>
                        </template>
                        <b-form-input class="custom-validity-fix gm-input" type="email" v-model="participant.email" size="sm"
                          :placeholder="$t('emailPlaceHolder')" required maxlength="100"
                          :state="participant.emailValidationState" />
                      </b-form-group>
                    </b-col>
                    <b-col md="3" class="mb-2 mb-md-0">
                      <b-form-group class="m-0">
                        <template v-slot:label>
                          <span class="small font-weight-bold text-dark-blue">{{ $t("phoneNumber") }}</span>
                        </template>
                        <b-form-input class="custom-validity-fix gm-input" type="tel" v-model="participant.phone" size="sm"
                          :placeholder="$t('phonePlaceHolder')" :pattern="phonePattern" maxlength="50"
                          @invalid="$event.target.setCustomValidity($t('invalidPhoneFormat'))"
                          @input.native="$event.target.setCustomValidity('')" />
                      </b-form-group>
                    </b-col>
                    <b-col md="1" class="d-flex align-items-center justify-content-center pb-1" style="gap: 8px;">
                      <b-button variant="outline-primary" size="sm" class="btn-icon-zoom" @click="onClickPickContact(participant)" v-b-tooltip.hover :title="$t('contact')">
                        <b-icon-person-lines-fill />
                      </b-button>
                      <b-button v-if="operators.length >= 2" variant="outline-danger" size="sm" class="btn-icon-zoom" @click="onClickDeleteOperator(i)" v-b-tooltip.hover :title="$t('actionDelete')">
                        <b-icon icon="trash-fill" />
                      </b-button>
                    </b-col>
                  </b-row>
                </div>
              </b-col>
            </b-row>

            <!-- 6. Loại cuộc họp (Security / Webinar Type) -->
            <b-row class="gm-form-row align-items-start py-3">
              <b-col md="3" class="gm-label-col">
                <label class="gm-label">
                  {{ $t("meetingType") }}
                </label>
              </b-col>
              <b-col md="9">
                <div class="d-flex flex-column" style="gap: 12px;">
                  <b-form-checkbox id="radio-group-public-meeting" v-model="privateMeeting" @change="checkboxMeeting" class="gm-checkbox font-weight-medium">
                    {{ $t("meetingTypeClosed") }}
                    <b-icon icon="question-circle-fill" shift-v="2" class="icon-info-zoom ml-1"
                      v-b-tooltip.hover="{ customClass: 'tooltip-class', placement: 'right', title: $t('privateSub1') }">
                    </b-icon>
                  </b-form-checkbox>
                  
                  <b-form-checkbox v-if="isWebinar" id="radio-group-public-webinar" v-model="webinarDefault" @change="onWebinarChange" class="gm-checkbox font-weight-medium">
                    {{ $t("webinar") }}
                    <b-icon icon="question-circle-fill" shift-v="2" class="icon-info-zoom ml-1"
                      v-b-tooltip.hover="{ customClass: 'tooltip-class', placement: 'right', title: $t('webinar_define') }">
                    </b-icon>
                  </b-form-checkbox>
                </div>

                <!-- Webinar Notification -->
                <div class="mt-3" v-if="isShowNoti">
                  <div class="gm-webinar-noti p-3 rounded">
                    <ul class="m-0 pl-3 small">
                      <li v-show="privateMeeting">{{ $t('webinar_noti_private') }}</li>
                      <li v-show="!privateMeeting">{{ $t('webinar_noti_public') }}</li>
                      <li>{{ $t('webinar_noti_2') }}</li>
                    </ul>
                  </div>
                </div>
              </b-col>
            </b-row>

            <!-- 6.5. Webinar normal participant count -->
            <b-row v-if="!privateMeeting && webinarDefault" class="gm-form-row align-items-center py-3">
              <b-col md="3" class="gm-label-col">
                <label class="gm-label">{{ $t("numberOfNormalMeeting") }}</label>
              </b-col>
              <b-col md="9">
                <div class="d-flex align-items-center" style="gap: 12px;">
                  <b-form-input required type="number" min="1" :max="maximumParticipants <= 300 ? maximumParticipants : 300" v-model="normalParticipantCount" class="gm-input" style="max-width: 120px;" />
                  <span class="small text-muted">
                    {{ $t("maxParticipantMeetings", { number: maximumParticipants <= 300 ? maximumParticipants : 300 }) }}
                  </span>
                </div>
              </b-col>
            </b-row>

            <!-- 7. Danh sách người tham gia (Participants List) -->
            <b-row v-if="privateMeeting || webinarDefault" class="gm-form-row align-items-start py-3">
              <b-col md="3" class="gm-label-col">
                <label class="gm-label">
                  {{ $t("meetingParticipantsList") }} <span v-if="!webinarDefault || privateMeeting" class="text-danger">(*)</span>
                </label>
              </b-col>
              <b-col md="9">
                <div class="gm-participants-container border rounded bg-white p-3">
                  <div class="d-flex align-items-center justify-content-between mb-2">
                    <span class="small font-weight-bold text-dark-blue">
                      ({{ $t("meetingParticipantsMaxCount", { count: maxParticipantsCount - 1 }) }})
                    </span>
                  </div>

                  <!-- Header row label -->
                  <b-form-row class="d-none d-md-flex align-items-center mb-1 px-3 text-secondary font-weight-bold small" style="flex-flow: nowrap;">
                    <b-form-group class="col-3 width-input m-0">
                      <template v-slot:label>{{ $t("fullName") }}</template>
                    </b-form-group>
                    <b-form-group class="col-3 width-input m-0">
                      <template v-slot:label>Email</template>
                    </b-form-group>
                    <b-form-group class="col-3 width-input m-0">
                      <template v-slot:label>{{ $t("phoneNumber") }}</template>
                    </b-form-group>
                    <b-form-group class="col-3 m-0">
                      <template v-slot:label>&nbsp;</template>
                    </b-form-group>
                  </b-form-row>

                  <!-- Virtual Scroller -->
                  <DynamicScroller ref="scroller" :items="participants" :min-item-size="40" key-field="uniqueKey"
                    class="gm-scroller bg-white rounded border">
                    <template v-slot="{ item: participant, index: i, active }">
                      <DynamicScrollerItem :item="participant" :active="active" :size-dependencies="[
                        participant.name,
                        participant.email,
                        participant.phone
                      ]" :data-index="i">
                        <div class="participant-row d-flex align-items-center py-2 px-2 border-bottom" style="gap: 8px;">
                          <span class="small text-muted" style="min-width: 24px;">({{ i + 1 }})</span>
                          
                          <div class="flex-grow-1">
                            <b-form-row style="flex-flow: nowrap; align-items: center;">
                              <b-form-group class="col-3 width-input m-0 pr-1">
                                <v-select class="vs-normalizer gm-vselect" label="label" :clearable="false" :filterable="false"
                                  :options="participant.typeaheadOptions" :clearSearchOnBlur="() => false"
                                  :clearSearchOnSelect="false" @search="participant.doSearchDebounced" 
                                  v-on:option:selecting="participant.onTypeaheadSelecting">
                                  <template v-slot:search="{ attributes, events }">
                                    <input v-bind="attributes" v-on="events" v-model="participant.name"
                                      :placeholder="$t('namePlaceHolder')" :pattern="fullNamePattern" class="form-control gm-input"
                                      minlength="2" maxlength="300" />
                                  </template>
                                </v-select>
                              </b-form-group>

                              <b-form-group class="col-3 width-input m-0 pr-1">
                                <b-form-input class="custom-validity-fix gm-input" type="email" v-model="participant.email"
                                  :placeholder="$t('emailPlaceHolder')" maxlength="100"
                                  :state="participant.emailValidationState" />
                              </b-form-group>

                              <b-form-group class="col-3 width-input m-0 pr-1">
                                <b-form-input class="custom-validity-fix gm-input" type="tel" v-model="participant.phone"
                                  :pattern="phonePattern" maxlength="50" :placeholder="$t('phonePlaceHolder')"
                                  @invalid="$event.target.setCustomValidity($t('invalidPhoneFormat'))"
                                  @input.native="$event.target.setCustomValidity('')" />
                              </b-form-group>

                              <b-form-group class="col-3 width-action m-0 d-flex align-items-center justify-content-between">
                                <div class="w-100 d-flex align-items-center justify-content-between">
                                  <b-button variant="link" class="text-danger p-0 text-decoration-none small" v-if="participants.length >= 2"
                                    @click="onClickDeleteParticipant(i)">
                                    {{ $t("actionDelete") }}
                                  </b-button>
                                  <span v-else>&nbsp;</span>
                                  
                                  <div class="face-badge-container">
                                    <img
                                      v-if="onFaceidForMeeting && participant.code && participant.imgUploadStatus == 'SUCCESS'"
                                      src="icon/faceId.svg" width="24px" height="24px" v-b-tooltip.hover="{
                                        customClass: 'tooltip-class',
                                        placement: 'bottom',
                                        title: $t('faceIdIconSub')
                                      }" />
                                    <img
                                      v-if="onFaceidForMeeting && (!participant.code || participant.imgUploadStatus != 'SUCCESS')"
                                      src="icon/warning.svg" width="24px" height="24px" />
                                  </div>
                                </div>
                              </b-form-group>
                            </b-form-row>
                          </div>
                        </div>
                      </DynamicScrollerItem>
                    </template>
                  </DynamicScroller>

                  <!-- Action buttons -->
                  <div class="d-flex flex-wrap mt-3" style="gap: 10px;">
                    <b-button v-if="!onFaceidForMeeting" variant="outline-primary" size="sm" @click="onClickAddParticipant">
                      <b-icon-plus class="mr-1" /> {{ $t("addMeetingParticipant") }}
                    </b-button>
                    <b-button variant="outline-success" size="sm" v-b-modal:new-import-excel>
                      <b-icon-file-earmark-spreadsheet class="mr-1" /> Chọn từ file
                    </b-button>
                    <b-button variant="outline-primary" size="sm" @click="onClickPickMultipleContacts(participants)">
                      <b-icon-person-lines-fill class="mr-1" /> {{ $t("chooseFromContacts") }}
                    </b-button>
                  </div>
                </div>
              </b-col>
            </b-row>

            <!-- 8. Cấu hình cuộc họp (Configs) -->
            <b-row v-if="meetingFeatures && meetingFeatures.length > 0" class="gm-form-row align-items-start py-3">
              <b-col md="3" class="gm-label-col">
                <label class="gm-label">
                  {{ $t("meetingConfig") }}
                </label>
              </b-col>
              <b-col md="9">
                <div class="d-flex flex-column" style="gap: 10px;">
                  <template v-for="feature in getFeatureWihoutWebinar()">
                    <!-- Feature thông thường -->
                    <div v-if="feature.featureCode !== 'ALLOW_WHITEBOARD'" :key="feature.featureCode">
                      <b-form-checkbox
                        v-model="feature.isActive"
                        @change="onFeatureChange(feature)"
                        class="gm-checkbox font-weight-medium"
                      >
                        {{ feature.featureName }}
                      </b-form-checkbox>
                    </div>

                    <!-- Whiteboard feature: checkbox + nút chọn bảng trắng -->
                    <div v-else :key="feature.featureCode + '_wb'" class="d-flex align-items-center" style="gap: 12px; flex-wrap: wrap;">
                      <b-form-checkbox
                        v-model="feature.isActive"
                        @change="onWhiteboardChange(feature)"
                        class="gm-checkbox font-weight-medium m-0"
                      >
                        {{ feature.featureName }}
                      </b-form-checkbox>
                      <b-button
                        v-if="feature.isActive && selectedWhiteboards.length === 0"
                        variant="outline-secondary"
                        size="sm"
                        class="btn-choose-whiteboard"
                        @click="$bvModal.show('whiteboard-picker-modal')"
                      >
                        <b-icon-plus class="mr-1" />{{ $t('chooseWhiteboard') }}
                      </b-button>
                      <!-- Custom pills when selected -->
                      <div v-if="feature.isActive && selectedWhiteboards.length > 0" class="d-inline-flex flex-wrap align-items-center" style="gap: 8px;">
                        <div v-for="wb in selectedWhiteboards" :key="wb.id" class="selected-whiteboard-pill d-inline-flex align-items-center">
                          <div class="pill-info-section" @click="$bvModal.show('whiteboard-picker-modal')">
                            <img :src="publicPath + 'images/whiteboard.svg'" alt="Whiteboard" style="width: 18px; height: 18px; vertical-align: middle; margin-right: 4px;" /><span class="whiteboard-link-text" v-b-tooltip.hover :title="wb.name">{{ wb.name }}</span>
                          </div>
                          <div class="pill-edit-section" @click="removeSelectedWhiteboard(wb.id)">
                            <b-icon-x style="width: 14px; height: 14px; color: white;" />
                          </div>
                        </div>
                        <b-button
                          variant="outline-secondary"
                          size="sm"
                          class="btn-choose-whiteboard ml-2"
                          @click="$bvModal.show('whiteboard-picker-modal')"
                        >
                          <b-icon-plus class="mr-1" />{{ $t('add') }}
                        </b-button>
                      </div>
                    </div>
                  </template>
                </div>
              </b-col>
            </b-row>

            <!-- Footer (Action buttons) -->
            <b-row :style="submitRowStyle" ref="submitButton" class="gm-submit-row pt-4 mb-4">
              <b-col md="3" class="d-none d-md-block"></b-col>
              <b-col :class="{ 'button-move': !isBottom, 'form-row': !isBottom }" md="9" class="p-0">
                <div :class="{ 'col-12': !isBottom }">
                  <!-- Form error msg -->
                  <div v-if="formErrorMsg" class="text-danger font-weight-semibold" :class="!isBottom ? 'text-center mb-4 mt-2' : 'mb-3'">
                    <b-icon icon="exclamation-circle-fill" class="mr-1" /> {{ formErrorMsg }}
                  </div>

                  <!-- Buttons -->
                  <div class="d-flex align-items-center" :class="{ 'justify-content-center my-3': !isBottom }" style="gap: 12px;">
                    <b-button v-if="meetingFeatures.length >= 0" :class="{ 'my-2': meetingFeatures.length <= 0 }"
                      style="padding: 6px 24px;" type="submit" variant="primary" :disabled="isSubmitting">
                      {{ submitButtonText }}
                    </b-button>
                    <router-link to="/meetings" class="btn btn-outline-secondary px-4 py-2">
                      {{ $t("cancel") || 'Hủy' }}
                    </router-link>
                  </div>
                </div>
              </b-col>
            </b-row>
          </b-form>
        </div>
      </div>

      <div style="width: 180px;">
        <!-- Khoảng trống dùng để hiện thị các nút action (danh bạ, xoá) nằm nổi ra bên ngoài -->
      </div>
    </b-container>

    <b-modal id="pick-contact-modal" :title="$t('chooseContact')" ok-only ok-variant="secondary"
      :ok-title="$t('cancel')">
      <PickContactModal modal-id="pick-contact-modal" @picked="onContactPicked" />
    </b-modal>
    <b-modal id="pick-multiple-contact-modal" :title="$t('chooseContact')" size="xl" hide-footer>
      <PickMultipleContactModal modal-id="pick-multiple-contact-modal"
        :p_selected-contacts="pickingMultipleContactArray" :onFaceidForMeeting="onFaceidForMeeting"
        @done="onMultipleContactPicked" />
    </b-modal>
    <!--Modal Import dữ liệu  @submitted="refreshData" -->
    <b-modal id="new-import-excel" :title="$t('importParticipants')" hide-footer no-close-on-backdrop size="md">
      <ImportExcelModal modal-id="new-import-excel" :activeFID="false" :typeModal="'meeting'"
        @imported="handleImportedParticipants"></ImportExcelModal>
    </b-modal>

    <!--Preview Modal-->
    <b-modal id="preview-import-modal" :title="$t('previewData')" hide-footer no-close-on-backdrop size="xl">
      <PreviewFileExcel modal-id="preview-import-modal" :activeFID="false" :initial-data="importData"
        :type-modal="'meeting'" @imported="handleFinalSave" />
    </b-modal>

    <!-- Whiteboard Picker Modal -->
    <b-modal
      id="whiteboard-picker-modal"
      :title="$t('chooseWhiteboard')"
      hide-footer
      size="lg"
      content-class="whiteboard-picker-modal-content"
      @show="onModalShow"
    >
      <div class="py-2">
        <div class="whiteboard-picker-wrapper">
          <div class="whiteboard-grid">
            <!-- Card Không Chọn -->
            <div
              class="whiteboard-card"
              :class="{ 'selected': tempSelectedWhiteboards.length === 0 }"
              @click="selectTempWhiteboard(null, '')"
            >
              <div class="whiteboard-thumbnail no-select-thumb">
                <img :src="publicPath + 'images/noSelect.svg'" alt="No Selection" style="width: 42px; height: 42px;" />
              </div>
              <div class="whiteboard-card-name">{{ $t('noSelectWhiteboard') }}</div>
              <div class="whiteboard-card-time wrap-text">{{ $t('noSelectWhiteboardDesc') }}</div>
            </div>

            <!-- Cards danh sách bảng trắng -->
            <div
              v-for="wb in whiteboardOptions"
              :key="wb.id"
              class="whiteboard-card"
              :class="{
                'selected': tempSelectedWhiteboards.some(w => w.id === wb.id),
                'disabled-card': wb.isUsed
              }"
              @click="wb.isUsed ? null : selectTempWhiteboard(wb.id, wb.name)"
            >
              <div class="whiteboard-thumbnail">
                <div v-if="wb.isUsed" class="whiteboard-used-status">
                  <div class="status-meeting-text">{{ $t('whiteboardInMeeting', { id: wb.meetingId }) }}</div>
                </div>
              </div>
              <div class="whiteboard-card-name">{{ wb.name }}</div>
              <div class="whiteboard-card-time">{{ wb.timeText }}</div>
            </div>
          </div>
        </div>

        <div class="d-flex align-items-center justify-content-end mt-4" style="gap: 12px;">
          <b-button
            variant="success"
            class="btn-create-other px-4 py-2"
            @click="onClickCreateOtherWhiteboard"
          >
            {{ $t('createOtherWhiteboard') }}
          </b-button>
          <b-button
            variant="primary"
            class="btn-confirm px-4 py-2"
            @click="onConfirmWhiteboardSelection"
          >
            {{ $t('confirm') }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Create Whiteboard Modal -->
    <CreateWhiteboardModal modal-id="create-whiteboard-modal" @created="handleWhiteboardCreated" />


  </main>
</template>

<style lang="scss" scoped>
::v-deep .custom-modal-width {
  max-width: 690px;
  width: 100%;
}

.gm-style-page {
  background-color: #f7f9fa;
  min-height: 100vh;
  font-size: 15.5px !important;
  color: #212529;

  .small, small {
    font-size: 13.5px !important;
  }
  
  .gm-container {
    background-color: #f2f5ff82;
    border-color: #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
  }

  .gm-header {
    background-color: transparent;
    border-bottom: 1px solid #e2e8f0;
    
    h4 {
      font-size: 20px;
      color: #212529;
    }
  }

  .text-dark-blue {
    color: #212529;
  }

  .text-gm-blue {
    color: #0E72ED;
  }

  .gm-form-row {
    border-bottom: 1px solid #f1f5f9;
    margin-left: 0;
    margin-right: 0;
    
    &:last-of-type {
      border-bottom: none;
    }
  }

  .gm-label-col {
    padding-left: 0;
    padding-top: 6px;
  }

  .gm-label {
    font-weight: 600;
    color: #212529;
    font-size: 16px;
    margin: 0;
  }

  .gm-input {
    border: 1px solid #cce3ff !important;
    border-radius: 6px !important;
    padding: 8px 12px !important;
    background-color: #ffffff !important;
    color: #212529 !important;
    font-size: 15.5px !important;
    height: auto !important;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;

    &:focus {
      border-color: #0E72ED !important;
      box-shadow: 0 0 0 2px rgba(14, 114, 237, 0.2) !important;
      outline: none !important;
    }

    &.custom-select {
      height: 38px !important;
    }

    &.select-time {
      max-width: 140px;
    }
  }

  input.gm-input,
  .custom-select.gm-input,
  .b-form-datepicker.gm-input,
  .b-form-btn-label-control.gm-input {
    height: 38px !important;
  }

  .input-group {
    .gm-input {
      border-top-right-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
    }
  }

  .gm-append-btn {
    border: 1px solid #cce3ff !important;
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    border-top-right-radius: 6px !important;
    border-bottom-right-radius: 6px !important;
    background-color: #ffffff !important;
    color: #6c757d !important;
    height: 100% !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease !important;

    &:hover {
      background-color: #f8fafc !important;
      color: #0E72ED !important;
      border-color: #0E72ED !important;
    }
  }

  ::v-deep .b-form-btn-label-control.gm-input {
    padding: 0 !important;
    height: 38px !important;
    display: flex !important;
    align-items: center !important;
    
    & > label {
      padding: 0 12px !important;
      line-height: 36px !important;
      font-size: 15.5px !important;
      margin: 0 !important;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: #212529 !important;
    }
    
    & > button {
      height: 36px !important;
      padding: 0 10px !important;
    }
  }

  .gm-vselect >>> .vs__dropdown-toggle {
    border: 1px solid #cce3ff !important;
    border-radius: 6px !important;
    padding: 2px 4px !important;
    background: white !important;
    
    &:focus-within {
      border-color: #0E72ED !important;
      box-shadow: 0 0 0 2px rgba(14, 114, 237, 0.2) !important;
    }

    .vs__selected,
    .vs__search {
      font-size: 15.5px !important;
      color: #212529 !important;
    }
  }

  .gm-operator-card {
    background-color: #f7f9fa;
    border-color: #e2e8f0 !important;
  }

  .gm-checkbox {
    margin-bottom: 6px;
    
    label {
      font-weight: 500;
      color: #212529;
      font-size: 15.5px;
      cursor: pointer;
    }
  }

  .icon-info-zoom {
    color: #747487 !important;
    font-size: 13px !important;
    cursor: help;
  }

  .gm-webinar-noti {
    background-color: #f0f6ff;
    border: 1px solid #d0e3ff;
    color: #1e3a8a;
  }

  .gm-participants-container {
    background-color: #ffffff;
    border-color: #e2e8f0;
  }

  .gm-scroller {
    max-height: 400px;
    min-height: 120px;
    overflow-y: auto;
    border-color: #e2e8f0 !important;
  }

  .participant-row {
    transition: background-color 0.15s;
    
    &:hover {
      background-color: #f8fafc;
    }
  }

  .btn-icon-zoom {
    border-radius: 4px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border-color: #cccccc;
    color: #212529;
    background: #fff;
    
    &:hover {
      background: #f1f5f9;
      color: #0E72ED;
      border-color: #0E72ED;
    }
  }

  .button-move {
    bottom: 20px;
    position: fixed !important;
    z-index: 10;
    width: calc(100% - 40px);
    max-width: 1240px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(8px);
    padding: 20px 24px;
    border: 1px solid rgba(226, 232, 240, 0.8) !important;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08) !important;
    border-radius: 12px !important;
  }

  .bg-light-red {
    background-color: #fef2f2;
    border-color: #fca5a5;
  }

  .width-input {
    flex: 0 0 29% !important;
    max-width: 29% !important;
  }

  .width-action {
    flex: 0 0 13% !important;
    max-width: 13% !important;
  }
}

/* Override scroller dropdown */
.gm-scroller >>> .vue-recycle-scroller__item-view {
  overflow: visible !important;
}

.gm-scroller >>> .vue-recycle-scroller__item-wrapper {
  overflow: visible !important;
}

.vue-recycle-scroller.ready.direction-vertical .vue-recycle-scroller__item-view {
  width: 100% !important;
}

.modern-inactive-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 24px;
  border-radius: 8px;
  background-color: #fff9f9;
  border: 1px solid #ffe8e8;
  color: #e53e3e;
  font-weight: 500;
  text-align: center;
}

.notice-icon {
  font-size: 24px;
  display: flex;
  align-items: center;
}

.empty-box {
  height: 150px;
}

/* Whiteboard Picker Modal Styles */
.whiteboard-picker-modal-content {
  max-width: 680px !important;
  margin: 1.75rem auto !important;
  border-radius: 16px !important;
  border: none !important;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1) !important;
  overflow: hidden;

  .modal-header {
    border-bottom: 1px solid #f1f5f9 !important;
    padding: 20px 24px !important;

    .modal-title {
      font-size: 20px !important;
      font-weight: 600 !important;
      color: #1e293b !important;
    }
  }

  .modal-body {
    padding: 24px !important;
    background: #ffffff !important;
  }

  .modal-footer {
    border-top: none !important;
    padding: 16px 24px 24px !important;
    display: flex;
    justify-content: flex-end;
    gap: 12px;

    .btn {
      border-radius: 8px !important;
      font-weight: 500 !important;
      font-size: 14px !important;
      padding: 10px 20px !important;
    }

    .btn-success {
      background-color: #00A859 !important;
      border-color: #00A859 !important;
      &:hover {
        background-color: #008f4c !important;
        border-color: #008f4c !important;
      }
    }

    .btn-primary {
      background-color: #0056B3 !important;
      border-color: #0056B3 !important;
      &:hover {
        background-color: #004494 !important;
        border-color: #004494 !important;
      }
    }
  }
}

.whiteboard-picker-wrapper {
  background-color: #F8F8FA;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e2e8f0;
}

.whiteboard-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  max-height: 360px;
  overflow-y: auto;
  padding: 4px;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; 
  scrollbar-width: none;  
}

.whiteboard-card {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  background: #ffffff;
  border: 2px solid transparent;
  border-radius: 12px;
  padding: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  align-items: center;

  &:hover:not(.disabled-card) {
    // transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: #cbd5e1;
  }

  &.selected {
    border-color: #0E72ED !important;
    box-shadow: 0 4px 16px rgba(14, 114, 237, 0.15) !important;
  }

  &.disabled-card {
    cursor: not-allowed;

    .whiteboard-card-name {
      color: rgba(0, 0, 0, 0.5) !important;
    }
  }

  .whiteboard-thumbnail {
    width: 100%;
    height: 70px;
    background-color: #F2F2F2;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
    transition: background-color 0.2s ease;

    &.no-select-thumb {
      .ban-icon {
        font-size: 32px;
        color: #ef4444;
      }
    }

    .whiteboard-used-status {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #28a745;
      font-size: 11px;
      font-weight: 500;
      line-height: 1.4;
      text-align: center;
      padding: 0 4px;
      white-space: pre-line;
    }
  }

  .whiteboard-card-name {
    font-size: 14px;
    font-weight: 600;
    color: #334155;
    text-align: center;
    margin-bottom: 4px;
    line-height: 1.4;
    height: 38px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
  }

  .whiteboard-card-time {
    font-size: 10px;
    color: #94a3b8;
    text-align: center;
    font-style: italic;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;

    &.wrap-text {
      white-space: normal !important;
      overflow: visible !important;
      text-overflow: clip !important;
    }
  }
}

.btn-choose-whiteboard {
  border-style: dashed !important;
  border-color: #28479B !important;
  color: #28479B !important;
  background-color: #EBEBEB !important;
  border-radius: 50rem !important;
  font-size: 13px !important;
  padding: 0 14px 0 8px !important;
  height: 32px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  transition: border-color 0.15s ease, color 0.15s ease, background-color 0.15s ease !important;

  &:hover {
    border-color: #1e3575 !important;
    color: #1e3575 !important;
    background-color: #dfdfdf !important;
  }
}

.selected-whiteboard-pill {
  border: 1px solid #28479B;
  border-radius: 8px;
  overflow: hidden;
  height: 32px;
  background-color: #EBEBEB;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
}

.pill-info-section {
  padding-left: 8px;
  padding-right: 12px;
  height: 100%;
  display: flex;
  align-items: center;
  color: #4b5563;
  
  .whiteboard-link-text {
    text-decoration: underline;
    color: #28479B;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 130px;
    display: inline-block;
  }

  &:hover {
    background-color: #dfdfdf;
  }
}

.pill-edit-section {
  background-color: #28479B;
  padding: 0 12px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1px solid #28479B;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #1e3575;
  }
}
</style>

<script lang="ts">
  import {Component, Ref} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";
  import * as util from "@/service/util";
  import * as meetingService from "@/service/meetingService";
  import * as subscriptionPlanService from "@/service/subscriptionPlanService";
  import * as validationUtils from "@/service/validationUtils";
  import PickContactModal from "@/components/contact/PickContactModal.vue";
  import PickMultipleContactModal from "@/components/contact/PickMultipleContactModal.vue";
  import * as contactService from "@/service/contactService";
  import * as userService from "@/service/userService";
  import {DateTime} from "luxon";
  import { Status } from '../enums/EnableStatus';
  import { ManagementType } from '../enums/subscriptionAction';
  import {featureCode} from "@/views/enums/featureCode";
  import { MeetingMode } from "@/views/enums/MeetingMode";
  import { WhiteboardStatus } from "@/views/enums/WhiteboardStatus";
  import { WEBINAR_URL } from '../enums/url';
  import ImportExcelModal from '@/components/modals/ImportExcelModal.vue';
  import PreviewFileExcel from '@/components/modals/PreviewFileExcel.vue';
  import * as whiteboardService from "@/service/whiteboardService";
  import CreateWhiteboardModal from "@/components/modals/CreateWhiteboardModal.vue";

  @Component({
    components: { PickContactModal, PickMultipleContactModal, ImportExcelModal, PreviewFileExcel, CreateWhiteboardModal }
  })
  export default class EditMeeting extends BaseComponent {

    enumManagementType = ManagementType;
    readonly datePickerFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
    readonly isPublicMeetingOptions = [
      { text: this.$t("meetingTypeClosed").toString(), value: false },
      { text: this.$t("meetingTypeOpen").toString(), value: true },
    ];

    // ⭐ WEBINAR
    isWebinar = false;
    webinarDefault = false;
    isShowNoti: boolean = false;

    id: number = null;
    isLoading = false;
    user = userService.getCurrentUser() || null;

    originalMeeting = null;
    activeFaceId = Status.DISABLE;
    activeVoiceText = this.user.activeVoiceText;
    onFaceidForMeeting = false;
    onVoiceText = false;

    // Whiteboard
    selectedWhiteboards: Array<{ id: string | number; name: string }> = [];
    whiteboardOptions: Array<{ id: string | number; name: string; timeText?: string; isUsed?: boolean; meetingId?: string }> = [];
    tempSelectedWhiteboards: Array<{ id: string | number; name: string }> = [];
    isWhiteboardLoaded = false;

    name = "";
    // numOfParticipants: number | string = null;
    participantsCount: number = 50;
    startDate: Date = null;
    startDateValidationStatus: boolean = null;

    startHour: number = null;
    hourOptions = [];

    startMinute: number = null;
    minuteOptions = [];

    durationHour = 1;
    durationHourOptions = [];

    durationMinute = 0;
    durationMinuteOptions = [];

    accessCode = "";
    publicMeeting = false;

    operators = [];
    participants = [];

    ///Loại mode (edit, clone) để gọi api
    meetingMode = null;

    /// Số người tham dự trong cuộc họp, mặc định lấy theo chi tiết cuộc họp
    // numOfParticipants = 0;
    onlyShowCameraForOperator = false;
    enableMeetingRecording = true;
    enableAutomaticMeetingRecording = false;
    enableMuteOnStart = true;
    enableCamera = true;
    enableMicro = true;
    enablePrivateChat = true;
    enablePublicChat = true;
    enableTakingNote = true;
    enableParticipationBeforeHost = false;
    enableSendAttachmentInChat = false;

    pickingContact = null;
    pickingMultipleContactArray = [];

    activeCustomerSubscription = null;
    activeSubscriptionPlan = null;
    storageInfo = null;

    formErrorMsg = "";
    isSubmitting = false;

    maximumParticipants = 0;

    // Flag để track component active
    _isActiveComponent = false;

    normalParticipantCount: number = 0;

    privateMeeting = false;

    // Biến để xử lý data từ modal
    importData: any[] = [];

    @Ref('submitButton') submitButton!: HTMLDivElement;
    isBottom = true;

    meetingFeatures = [];

    async mounted() {

      this._isActiveComponent = true;

      window.addEventListener('scroll', this.handleScroll);
      this.id = +this.$route.params.id || null;

      this.hourOptions.push({ text: this.$t("hh"), value: null, disabled: true });
      for (let i = 0; i < 24; i++) {
        this.hourOptions.push({
          text: `${i.toString().padStart(2, "0")} ` + this.$t("hours"),
          value: i
        });
      }

      this.minuteOptions.push({ text: this.$t("mm"), value: null, disabled: true });
      for (let i = 0; i < 60; i++) {
        this.minuteOptions.push({
          text: `${i.toString().padStart(2, "0")} ` + this.$t("minutes"),
          value: i
        });
      }

      this.durationHourOptions = [];
      this.durationHourOptions.push({
        text: this.$t("hh"),
        value: null,
        disabled: true
      });

      for (let i = 0; i <= 24; i++) {
        this.durationHourOptions.push({
          text: `${i.toString().padStart(2, "0")} ` + this.$t("hours"),
          value: i
        });


      }
      this.durationMinuteOptions = [];
      this.durationMinuteOptions.push({
        text: this.$t("mm"),
        value: null,
        disabled: true
      });

      for (let i = 0; i < 60; i += 15) {
        this.durationMinuteOptions.push({
          text: `${i.toString().padStart(2, "0")} ` + this.$t("minutes"),
          value: i
        });
      }


      this.fetchMeeting();

      ///
      this.user = await userService.getUserDetail();

      ///Kiểm tra có lấy được giá trị mode
      //Nếu không lấy được mặc định là edit
      this.meetingMode = this.$route.meta.mode ?? MeetingMode.EDIT;

    }

    beforeDestroy() {
      // Đánh dấu component không active nữa
      this._isActiveComponent = false;
    }

    handleScroll() {
      let clientHeight = document.documentElement.clientHeight;
      if (this.submitButton) {
        let elementToTop = this.submitButton.getBoundingClientRect().top;
        this.isBottom = clientHeight > elementToTop;
      }
    }
    /**
     * Cấu hình feature FaceID của khách hàng
     */
    get authFaceIdFeature() {
      return (this.meetingFeatures ?? []).find(
        f => f.featureCode === featureCode.AUTH_FACEID
      ) ?? null;
    }

    /**
     * Mục đích có thể lấy được giá trị meeting.onFaceidForMeeting khi KH nhấn vào checkbox
     */
    checkboxMeeting() {
      const authFaceId = this.authFaceIdFeature;
      // Nếu không có feature hoặc bị DISABLE → luôn OFF
      const isFaceIdAllowed = authFaceId && authFaceId.isActive !== false;

      // Áp dụng logic
      this.onFaceidForMeeting = isFaceIdAllowed
        ? (this.activeFaceId && this.privateMeeting)
        : false;
      if (this.privateMeeting) {
        this.normalParticipantCount = 0;
      }
      this.handleScroll();
    }

    async fetchMeeting() {
      try {
        this.isLoading = true;

        // Start fetching the original meeting
        const fetchMeetingPromise = meetingService.getDetails(this.id);

        // Fetch customer subscription
        const subscriptions = await subscriptionPlanService.getCustomerSubscription(this.getUserCustomerId());
        this.activeCustomerSubscription = subscriptions.find(it => it.isActive);
        if (this.activeCustomerSubscription) {
          const { subscriptionPlanId } = this.activeCustomerSubscription;
          this.activeSubscriptionPlan = await subscriptionPlanService.getPackageDetail(subscriptionPlanId);

          const maxDurationHours = this.activeSubscriptionPlan.maximumLengthOfOneMeeting / 60;
          // _.remove(this.durationHourOptions, it => it > maxDurationHours);
          this.durationHourOptions = this.durationHourOptions.filter(it => it.value === null || it.value <= maxDurationHours);
        }

        // Obtain fetching result
        const meeting = await fetchMeetingPromise;

        // Assign props from the original meeting object
        this.name = meeting.name;
        // this.numOfParticipants = meeting.numOfParticipants;
        this.accessCode = meeting.accessCode;

        const startDateTime = DateTime.fromISO(meeting.scheduleStartTime);
        this.startDate = startDateTime.toJSDate();
        this.startHour = startDateTime.hour;
        this.startMinute = startDateTime.minute;

        ///Lấy số lượng người tham gia lớn nhất tại thời điểm tạo cuộc họp
        this.maximumParticipants = meeting.numOfParticipants;
        this.participantsCount = meeting.numOfParticipants || 50;
        this.normalParticipantCount = meeting.webinarThreshold;


        ///
        this.durationHour = Math.floor(meeting.scheduleDuration / 60);
        this.durationMinute = meeting.scheduleDuration % 60;

        this.privateMeeting = !meeting.publicMeeting;
        this.onFaceidForMeeting = meeting.activeFaceId;
        this.onVoiceText = meeting.activeVoiceText;

        const contactItems = meeting.meetingParticipants
          .map(it => Object.assign(
            this.createContactItem(it.role),
            _.pick(it, ["uniqueKey", "contactId", "name", "email", "phone", "code", "imgUploadStatus", "organization"])
          ));
        this.operators = contactItems.filter(it => it.role === "OPERATOR");
        this.participants = contactItems.filter(it => it.role !== "OPERATOR");

        if (!this.participants.length) {
          this.onClickAddParticipant(); // prevent zero participants
        }

        // this.numOfParticipants = meeting.numOfParticipants;
        this.onlyShowCameraForOperator = meeting.onlyShowCameraForOperator;
        this.enableMeetingRecording = meeting.enableMeetingRecording;
        this.enableAutomaticMeetingRecording = meeting.enableAutomaticMeetingRecording;
        this.enableMuteOnStart = meeting.enableMuteOnStart;
        this.enableCamera = meeting.enableCamera;
        this.enableMicro = meeting.enableMicro;
        this.enablePrivateChat = meeting.enablePrivateChat;
        this.enablePublicChat = meeting.enablePublicChat;
        this.enableTakingNote = meeting.enableTakingNote;
        this.enableParticipationBeforeHost = meeting.enableParticipationBeforeHost;

        this.originalMeeting = meeting;

        this.storageInfo = await meetingService.getStoragePlan();
        if (!this.storageInfo.allowRecording) {
          this.enableAutomaticMeetingRecording = false;
          this.enableMeetingRecording = false;
        }

        this.meetingFeatures = meeting.meetingFeatures;
        if (Array.isArray(this.meetingFeatures)) {
          this.meetingFeatures.forEach(element => {
            element.isActive = element.isActive == 1 ? true : false;
          });
        };

        // ⭐ WEBINAR
        const webinarFeature = this.meetingFeatures.find(
          f => f.featureCode === featureCode.ALLOW_WEBINAR
        );

        this.isWebinar = !!webinarFeature;
        this.webinarDefault = this.isShowNoti = !!webinarFeature?.isActive;

        // set activeFaceId theo dữ liệu ban đầu
        const faceFeature = this.meetingFeatures.find(f => f.featureCode === featureCode.AUTH_FACEID);
        this.activeFaceId = faceFeature && faceFeature.isActive ? 1 : 0;

        // Load whiteboard info
        this.selectedWhiteboards = (meeting.whiteboards || []).map(wb => ({
          id: wb.boardUid,
          name: wb.name
        }));
      } catch (e) {
        this.toastError(e);

      } finally {
        this.isLoading = false;
        this.handleScroll();
      }
    }

    get isBBB(): boolean {
      return this.activeSubscriptionPlan?.coreImplName?.toLowerCase() === "bbb";
    }

    get isZoom(): boolean {
      return this.activeSubscriptionPlan?.coreImplName?.toLowerCase() === "zoom";
    }

    /**
     * Max num of participants (based on the active subscription)
     */
    get subscriptionPlanMaxParticipantsCount(): number {
      return this.activeSubscriptionPlan?.maximumNumberOfParticipantsInMeeting || 0;
    }

    /**
     * Lấy số lượng người tham gia lớn nhất của gói cước đang hoạt động ?? dựa theo gói cước hệ thống.
     */
    get maxParticipantsCount(): number {
      return this.activeCustomerSubscription?.meetingConcurrentParticipantsLimit ?? this.subscriptionPlanMaxParticipantsCount;
    }

    get durationValidationStatus(): boolean {
      const maxDurationMins = this.activeSubscriptionPlan?.maximumLengthOfOneMeeting || 0;
      const durationMins = this.durationHour * 60 + this.durationMinute;
      return (durationMins < 15 || durationMins > maxDurationMins) ? false : null;
    }

    get maxDurationStr(): string {
      const maxDurationMins = this.activeSubscriptionPlan?.maximumLengthOfOneMeeting || 0;
      const hours = Math.floor(maxDurationMins / 60);
      const mins = maxDurationMins % 60;

      return [
        hours > 0 ? hours + " " + this.$t("hours").toString() : undefined,
        mins > 0 ? mins + " " + this.$t("minutes").toString() : undefined
      ].filter(_.identity).join(" ");
    }



    get submitRowStyle() {
      if (this.formErrorMsg) return { height: '140px' };
      return { height: '38px' };
    }

    setRandomAccessCode() {
      this.accessCode = util.randomStr(6).toUpperCase();
      util.clearCustomValidity();
    }

    createContactItem(role): any {
      const p = {
        uniqueKey: `${Date.now()}-${util.generateUUID()}`,
        name: "",
        phone: "",
        email: "",
        assignedBy: this.userId,
        role,
        emailValidationState: null,
        typeaheadOptions: [],

        doSearchDebounced: _.debounce(async function (q: string, loading: (p: boolean) => void) {
          try {
            if (q.length < 2) {
              p.typeaheadOptions = [];
            } else {
              loading(true);

              // Fetch and assign typeahead options
              p.typeaheadOptions = (await contactService.getAllContacts(1, 10, q)).data;

              // Typeahead option label: "name - department"
              for (const contactOption of p.typeaheadOptions) {
                contactOption.label = [contactOption.name, contactOption.department, contactOption.organization]
                  .map(it => it?.trim())
                  .filter(_.identity)
                  .join(" - ");
              }
            }

          } catch (e) {
            console.error(e);

          } finally {
            loading(false);
          }
        }, 300),

        onTypeaheadSelecting(option) {
          p.name = option.name;
          p.phone = option.phone;
          p.email = option.email;
          util.clearCustomValidity();
        }
      };

      return p;
    }

    onClickAddOperator() {
      this.operators.push(this.createContactItem("OPERATOR"));
    }

    onClickAddParticipant() {
      this.participants.push(this.createContactItem("PARTICIPANT"));
      this.handleScroll();
      // Scroll danh sách xuống cuối
      this.scrollToBottom();
    }

    onClickDeleteOperator(index: number) {
      this.operators.splice(index, 1);
    }

    onClickDeleteParticipant(index: number) {
      this.participants.splice(index, 1);
    }

    onClickPickContact(contact) {
      this.pickingContact = contact;
      this.$bvModal.show("pick-contact-modal");
    }

    onClickPickMultipleContacts(targetArray: any[]) {
      this.pickingMultipleContactArray = targetArray.filter(participant => participant.email);
      this.$bvModal.show("pick-multiple-contact-modal");
      // Scroll danh sách xuống cuối
      this.scrollToBottom();
    }

    onContactPicked(contact) {
      this.pickingContact.name = contact.name;
      this.pickingContact.email = contact.email;
      this.pickingContact.phone = contact.phone;

      // HTMLInputElement doesn't emit "input" event when setting the value programmatically
      // so we need to call setCustomValidity on our own
      util.clearCustomValidity();
    }

    onMultipleContactPicked(contacts: any[]) {
      this.formErrorMsg = null;

      // delete old
      this.participants = this.participants.filter(p => !p.id && !p.contactId);
      if (contacts.length) {
        contacts = contacts.map((p, index) => ({
          ...p,
          uniqueKey: `${Date.now()}-${util.generateUUID()}`,
        }));
        this.participants = this.participants.filter(p => ["name", "email", "phone"].some(k => p[k]));
      }
      contacts.forEach(contact => {
        this.convertContactToParticipant(contact);
      });
      // push new
      this.participants.push(...contacts);

      if (!this.participants.length) {
        this.onClickAddParticipant();
      }
      const timeout = setTimeout(() => {
        this.handleScroll();
        clearTimeout(timeout);
      }, 200);
      // Scroll danh sách xuống cuối
      this.scrollToBottom();
    }

    convertContactToParticipant(contact) {
      contact.doSearchDebounced = () => {
        return;
      };
      contact.onTypeaheadSelecting = option => {
        return;
      };
    }

    /// Cuộn refs về cuối dùng
    scrollToBottom() {
      this.$nextTick(() => {

        // Early return nếu component không active
        if (!this._isActiveComponent) {
          return;
        }

        const scroller = this.$refs.scroller as any;

        // Kiểm tra đầy đủ
        if (!scroller) {
          return;
        }

        if (!scroller.scrollToItem) {
          return;
        }

        const lastIndex = this.participants.length - 1;

        try {
          scroller.scrollToItem(lastIndex);
        } catch (error) {
        }
      });
    }

    async submitForm() {
      this.startDateValidationStatus = null;
      this.formErrorMsg = null;

      const isWebinar = this.webinarDefault;
      const isPrivateOrWebinar = this.privateMeeting || this.webinarDefault;

      let allParticipants = isPrivateOrWebinar
        ? [...this.operators, ...this.participants]
        : this.operators;




      for (const p of allParticipants) {
        p.emailValidationState = null;
        p.contactId = p.id ? p.id : p.contactId;
      }


      if (!this.startDate) {
        this.startDateValidationStatus = false;
        this.formErrorMsg = this.$t("pleaseSelectMeetingStartDate").toString();
        return;
      }

      if (_.isNil(this.startDate) || _.isNil(this.startHour) || _.isNil(this.startMinute)) {
        this.formErrorMsg = this.$t("pleaseSelectMeetingStartDatetime").toString();
        return;
      }

      const startDateTime = DateTime.fromJSDate(this.startDate).set({
        hour: this.startHour,
        minute: this.startMinute
      });

      if (+startDateTime.diffNow() < 0) {
        this.formErrorMsg = this.$t("invalidMeetingStartDatetime").toString();
        return;
      }


      let invalidInfoMap = new Map<number, any>();

      const validateList = isWebinar ? this.operators : allParticipants;

      for (const [index, p] of validateList.entries()) {
        p.email = p.email?.toLowerCase();
        p.name = p.name?.trim();

        if (!validationUtils.isEmail(p.email)) {
          p.emailValidationState = false;
          invalidInfoMap.set(index, p);
        }

        if (!p.name || p.name.length < 2) {
          invalidInfoMap.set(index, p);
        }
      }

      if (invalidInfoMap.size > 0) {
        const invalidRows = [...invalidInfoMap.keys()]
          .map(i => this.$t("row") + " " + i)
          .join(", ");
        this.formErrorMsg =
          this.$t("invalidMeetingOperatorOrParticipantEmail").toString() +
          ": " +
          invalidRows;
        return;
      }

      if (this.participantsCount > this.maxParticipantsCount) {
        this.formErrorMsg = this.$t("maxMeetingParticipantsExceeded").toString();
        return;
      }

      if (this.durationValidationStatus === false) {
        this.formErrorMsg = this.$t("invalidMeetingDurationOrExceededLimit").toString();
        return;
      }


      const filteredParticipants = allParticipants.filter(p => {

        if (p.role !== "PARTICIPANT") return true;


        if (isWebinar) {
          return !!p.name?.trim();
        }

        return (
          !!p.name?.trim() ||
          !!p.email?.trim() ||
          !!p.phone?.trim()
        );
      });

      const meetingFeatures = this.meetingFeatures
        .filter(f => f.isActive)
        .map(f => f.featureCode);

      const data = {
        id: this.id,
        status: this.originalMeeting.status,
        name: this.name,
        numOfParticipants: this.participantsCount,
        scheduleStartTime: startDateTime.toISO(),
        scheduleDuration: this.durationHour * 60 + this.durationMinute,
        operatorPassword: this.accessCode,
        accessCode: this.accessCode,
        publicMeeting: !this.privateMeeting,

        onlyShowCameraForOperator: this.isBBB ? this.onlyShowCameraForOperator : false,
        enableMeetingRecording: this.isBBB ? this.enableMeetingRecording : false,
        enableAutomaticMeetingRecording: this.enableAutomaticMeetingRecording,
        enableMuteOnStart: this.isBBB ? this.enableMuteOnStart : false,
        enableCamera: this.enableCamera,
        enableMicro: this.isBBB ? this.enableMicro : false,
        enablePrivateChat: this.isBBB ? this.enablePrivateChat : false,
        enablePublicChat: this.isBBB ? this.enablePublicChat : false,
        enableTakingNote: this.isBBB ? this.enableTakingNote : false,
        enableParticipationBeforeHost: this.isZoom
          ? this.enableParticipationBeforeHost
          : false,
        enableSendAttachmentInChat: this.isBBB
          ? this.enableSendAttachmentInChat
          : false,

        participants: filteredParticipants.map(it =>
          _.pick(it, [
            "contactId",
            "name",
            "phone",
            "email",
            "assignedBy",
            "role",
            "code",
            "imgUploadStatus",
            "organization"
          ])
        ),

        activeFid:
          this.onFaceidForMeeting && this.privateMeeting
            ? Status.ENABLE
            : Status.DISABLE,

        activeVoiceText: this.onVoiceText ? Status.ENABLE : Status.DISABLE,
        meetingFeatures,
        webinarThreshold: this.normalParticipantCount,
        boardUids: this.selectedWhiteboards.map(wb => wb.id.toString())
      };

      try {
        this.isSubmitting = true;

        switch (this.meetingMode) {
          case MeetingMode.CLONE: {
            const { id: newMeetingId } = await meetingService.createMeeting(data);
            this.meetingMode = MeetingMode.EDIT;
            this.$router.push("/meetings/details/" + newMeetingId);
            this.toastSuccessDelay(this.$t("createMeetingSuccessful").toString());
            break;
          }

          default:
            await meetingService.updateMeeting(data);
            this.$router.push("/meetings/details/" + this.id);
            this.toastSuccessDelay(this.$t("updateSuccessful").toString());
            break;
        }

      } catch (e) {
        this.formErrorMsg = this.$t(e.response?.data?.code || e.code).toString();
        this.toastError(e);
      } finally {
        this.isSubmitting = false;
      }
    }

    /**
     * Lắng nghe sự kiện thay đổi trạng thái của các feature
     * Hiện tại chỉ có FaceID
     * @param feature 
     */
    onFeatureChange(feature) {
      if (feature.featureCode === featureCode.AUTH_FACEID) {
        const authFaceId = this.authFaceIdFeature;
        if (authFaceId) {
          this.activeFaceId = feature.isActive ? Status.ENABLE : Status.DISABLE;
          this.onFaceidForMeeting = this.activeFaceId === Status.ENABLE;
        }
      }
    }
    /**
     * Nếu clone hiển thị text trên button là 'tạo cuộc họp'
     * Nếu edit hiển thị text trên button là 'Lưu thay đổi'
     */
    get submitButtonText() {
      return this.meetingMode === MeetingMode.CLONE
        ? this.$t('createMeeting')
        : this.$t('saveChanges');
    };
    //
    getFeatureWihoutWebinar() {
      return Array.isArray(this.meetingFeatures)
        ? this.meetingFeatures.filter(
          f => f.featureCode !== featureCode.ALLOW_WEBINAR
        )
        : [];
    }
    // Sự kiện check chọn webinar
    onWebinarChange(value) {

      this.isShowNoti = value;
      const webinarFeature = this.meetingFeatures.find(
        f => f.featureCode === featureCode.ALLOW_WEBINAR
      );
      if (webinarFeature) {
        webinarFeature.isActive = value;
      }
      if (!value) {
        this.normalParticipantCount = 0;
      }
    }

    get whiteboardFeature() {
      return (this.meetingFeatures ?? []).find(f => f.featureCode === featureCode.ALLOW_WHITEBOARD);
    }

    removeSelectedWhiteboard(id: string | number) {
      this.selectedWhiteboards = this.selectedWhiteboards.filter(w => w.id !== id);
    }

    async onWhiteboardChange(feature: any) {
      if (feature && feature.isActive) {
        await this.loadWhiteboardOptions();
      } else {
        this.selectedWhiteboards = [];
        this.whiteboardOptions = [];
        this.isWhiteboardLoaded = false;
      }
    }

    async loadWhiteboardOptions(force = false) {
      if (this.isWhiteboardLoaded && !force) return;
      this.whiteboardOptions = [];
      try {
        this.showLoading();
        const request = {
          page: 1,
          pageSize: 1000,
          sorts: [],
        };
        const result = await whiteboardService.getWhiteboards(request);
        if (result && result.data) {
          this.whiteboardOptions = result.data.map(wb => ({
            id: wb.boardUid,
            name: wb.name,
            timeText: wb.modifiedAt
              ? this.$t('updatedWbText', { time: util.formatWbDateTime(wb.modifiedAt) }).toString()
              : this.$t('createdWbText', { time: util.formatWbDateTime(wb.createdAt) }).toString(),
            isUsed: wb.status === WhiteboardStatus.IN_USE,
            meetingId: wb.inProgressMeeting?.id,
          }));
          this.isWhiteboardLoaded = true;
        }
      } catch (e) {
        this.toastError(e);
      } finally {
        this.hideLoading();
      }
    }

    async onModalShow() {
      this.tempSelectedWhiteboards = [...this.selectedWhiteboards];
      await this.loadWhiteboardOptions();
    }

    selectTempWhiteboard(id: string | number | null, name: string) {
      if (id === null) {
        this.tempSelectedWhiteboards = [];
      } else {
        const index = this.tempSelectedWhiteboards.findIndex(w => w.id === id);
        if (index > -1) {
          this.tempSelectedWhiteboards.splice(index, 1);
        } else {
          this.tempSelectedWhiteboards.push({ id, name });
        }
      }
    }

    onConfirmWhiteboardSelection() {
      this.selectedWhiteboards = [...this.tempSelectedWhiteboards];
      this.$bvModal.hide('whiteboard-picker-modal');
    }

    onClickCreateOtherWhiteboard() {
      this.$bvModal.show('create-whiteboard-modal');
    }

    handleWhiteboardCreated(newWb: any) {
      if (newWb) {
        const mappedWb = {
          id: newWb.boardUid,
          name: newWb.name,
          timeText: newWb.modifiedAt
            ? this.$t('updatedWbText', { time: util.formatWbDateTime(newWb.modifiedAt) }).toString()
            : this.$t('createdWbText', { time: util.formatWbDateTime(newWb.createdAt) }).toString(),
          isUsed: newWb.status === WhiteboardStatus.IN_USE && (() => {
            if (newWb.meetings && newWb.meetings.length > 0) {
              return newWb.meetings.some(m => m.id !== this.id);
            }
            const currentMeetingId = newWb.inProgressMeeting?.id || newWb.InProgressMeeting?.id || newWb.meetingId || newWb.meeting?.id;
            if (currentMeetingId) {
              return currentMeetingId !== this.id;
            }
            return true;
          })(),
          meetingId: newWb.meetings && newWb.meetings.length > 0 ? newWb.meetings.map(m => m.id).join(', ') : (newWb.inProgressMeeting?.id || newWb.InProgressMeeting?.id || newWb.meetingId || newWb.meeting?.id || '')
        };
        this.whiteboardOptions.unshift(mappedWb);
        if (!this.tempSelectedWhiteboards.some(w => w.id === mappedWb.id)) {
          this.tempSelectedWhiteboards.push({ id: mappedWb.id, name: mappedWb.name });
        }
      }
    }

    OpenDetail() {
      window.open(WEBINAR_URL.WEBINAR_URL_GUIDE, '_blank');
    }

    /**
 * Tạo key duy nhất cho participant
 * Dùng để:
 * - So sánh dữ liệu trùng khi import
 * - Chuẩn hóa dữ liệu trước khi check
 *
 * Format key: name|email|phone
 */
    private buildParticipantKey(p: any): string {
      const name = (p.name || "").trim().toLowerCase();   // Chuẩn hóa name
      const email = (p.email || "").trim().toLowerCase(); // Chuẩn hóa email
      const phone = (p.phone || "").toString().trim();    // Chuẩn hóa phone

      return `${name}|${email}|${phone}`;
    }

    /**
     * Nhận dữ liệu sau khi import file Excel
     * - Gán data vào state
     * - Đóng modal import
     * - Mở modal preview nếu data hợp lệ
     */
    handleImportedParticipants(data: any[]) {

      // 1. Gán dữ liệu import vào state
      this.importData = data;

      // 2. Đóng modal chọn file import
      this.$bvModal.hide('new-import-excel');

      // 3. Delay để modal đóng xong rồi mới mở preview
      setTimeout(() => {
        if (this.importData && this.importData.length > 0) {
          // Có dữ liệu → mở modal preview
          this.$bvModal.show('preview-import-modal');
        } else {
          // Data rỗng hoặc lỗi parse file
          console.error("Lỗi file rỗng hoặc lỗi file. Xem lại file");
        }
      }, 300);
    }

    /**
     * Xử lý lưu dữ liệu sau khi đóng popup preview
     * - Loại bỏ dữ liệu trùng
     * - Kiểm tra giới hạn số lượng
     * - Insert dữ liệu hợp lệ vào participants
     */
    handleFinalSave(finalData: any[]) {
      // Không có dữ liệu thì bỏ qua
      if (!finalData || finalData.length === 0) return;

      /**
       * Lấy danh sách participant hiện có
       * Chỉ giữ những dòng có dữ liệu thật
       */
      const existingParticipants = this.participants.filter(
        p => p.name || p.email || p.phone
      );

      /**
       * Set chứa các key participant đã tồn tại
       * Giúp check trùng nhanh O(1)
       */
      const existingKeySet = new Set(
        existingParticipants.map(p => this.buildParticipantKey(p))
      );

      const importedParticipants = []; // Danh sách sẽ insert
      let duplicateCount = 0;           // Đếm số dòng trùng

      /**
       * Duyệt từng dòng import từ preview
       */
      for (const row of finalData) {
        const newP = this.createContactItem("PARTICIPANT");

        // Map dữ liệu từ file
        newP.name = (row.fullname || "").trim();
        newP.email = (row.email || "").toLowerCase().trim();
        newP.phone = (row.phone || "").toString().trim();
        newP.imgUploadStatus = "NONE";

        // Tạo key để check trùng
        const key = this.buildParticipantKey(newP);

        // ❌ Trùng toàn bộ (name + email + phone) → bỏ qua
        if (existingKeySet.has(key)) {
          duplicateCount++;
          continue;
        }

        // ✅ Chưa trùng → thêm vào danh sách insert
        existingKeySet.add(key);
        importedParticipants.push(newP);
      }

      /**
       * Check giới hạn số lượng participant
       * (phải check TRƯỚC khi insert)
       */
      const totalCount =
        existingParticipants.length + importedParticipants.length;

      if (totalCount > this.maxParticipantsCount) {
        this.toastErrorMessage(this.$t("importMaximum", { number: this.maxParticipantsCount }));
        return;
      }

      /**
       * Gộp participant cũ + participant import hợp lệ
       */
      this.participants = [
        ...existingParticipants,
        ...importedParticipants
      ];

      // Đóng modal preview
      this.$bvModal.hide('preview-import-modal');

      /**
       * Sau khi DOM render xong
       * → xử lý scroll UI
       */
      this.$nextTick(() => {
        this.handleScroll();
        this.scrollToBottom();
      });

      /**
       * Thông báo kết quả import
       */
      if (duplicateCount > 0) {
        // Có dữ liệu bị trùng
        this.toastErrorMessage(
          this.$t("importPass", { number: duplicateCount }).toString()
        );
      }

      // Import thành công
      this.toastSuccessDelay(
        this.$t("importSuccess", { number: importedParticipants.length }).toString()
      );
    }
  }
</script>